import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  Globe,
  HeartPulse,
  Instagram,
  MapPin,
  Phone,
  Facebook,
  Youtube,
} from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { HEALTH_PROVIDERS } from './HealthWellness';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

// Same fallback the category page hero uses, for providers without an image.
const FALLBACK_IMAGE =
  'https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-07/bcef0e4e86b9.png';

// Same key function HealthWellness.tsx uses for its managed collection, so the
// slug in the URL always matches the entry the grid card linked from.
export function healthSlug(p: any, i: number) {
  return slugify(p.name || String(i));
}

// The platform datatable declares the multiselect as a `string` column, so a
// saved array comes back comma-joined. Split it apart (same as HealthWellness.tsx).
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((x) => x.trim()).filter(Boolean);
  return [];
}

// Collect every contact/social URL a listing has, deduped, with a label and
// icon — mirrors the button logic on the grid cards.
function collectContacts(p: any) {
  const candidates: { url?: string; label?: string }[] = [
    { url: p.website, label: 'Website' },
    { url: p.website2, label: 'Website' },
    { url: p.instagram, label: 'Instagram' },
    { url: p.facebook, label: 'Facebook' },
    { url: p.youtube, label: 'YouTube' },
    { url: p.tiktok, label: 'TikTok' },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => {
      const u = c.url.toLowerCase();
      if (u.includes('facebook.com')) return { ...c, Icon: Facebook, label: 'Facebook' };
      if (u.includes('youtube.com') || u.includes('youtu.be')) return { ...c, Icon: Youtube, label: 'YouTube' };
      if (u.includes('instagram.com')) return { ...c, Icon: Instagram, label: 'Instagram' };
      return { ...c, Icon: Globe, label: c.label || 'Website' };
    });
}

export default function HealthWellnessDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('health_wellness', HEALTH_PROVIDERS, (p, i) => healthSlug(p, i));

  // Match the SAME key the listing grid links with (`item._key` — the default
  // entry's `id` field, or the slugified card name for admin-added cards). The
  // old lookup used slugify(name), which differs from the URL whenever a
  // default's `id` isn't the slugified name (e.g. id "dr-zille-shah" vs name
  // "Dr. Zille Shah MD" -> "dr-zille-shah-md"), making every such card 404 as
  // "Provider not found". healthSlug stays as a fallback for any
  // externally-linked legacy URL that used the name slug, and `_mcId` keeps
  // OLD row-id URLs (e.g. /health-wellness/6a6b16e2…) resolving after
  // admin-added cards switched to name-based keys.
  const entry = mc.items.find(
    (p: any, i: number) =>
      p._key === slug || p._mcId === slug || healthSlug(p, i) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Health & Wellness Provider Not Found | Dallas Desi Scoop"
          description="This health and wellness provider could not be found."
          canonical="/health-wellness"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Provider not found.</p>
        <Link to="/health-wellness" className="text-[#082f6d] font-semibold hover:underline">
          ← Back to Health &amp; Wellness
        </Link>
      </div>
    );
  }

  const name = entry.name || 'Health & Wellness Provider';
  const image = entry.image || FALLBACK_IMAGE;
  const types = toTypeArray(entry.type);
  const typeLabel = types.length > 0 ? types.join(' · ') : 'Health & Wellness Provider';
  const clinic = typeof entry.clinic === 'string' ? entry.clinic.trim() : '';
  const specialty = typeof entry.specialty === 'string' ? entry.specialty.trim() : '';
  const address = typeof entry.address === 'string' ? entry.address.trim() : '';
  const location = typeof entry.location === 'string' ? entry.location.trim() : '';
  const cityLabel = location && location !== 'Other' ? location : 'Dallas–Fort Worth';
  const phone = typeof entry.phone === 'string' ? entry.phone.trim() : '';
  const contacts = collectContacts(entry);
  const hasAnySocial = countSocialLinks(entry) > 0;
  const googleMapsUrl =
    entry.mapsUrl || (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '');
  const canonicalPath = `/health-wellness/${slug}`;

  const title = `${name} | ${typeLabel} in ${cityLabel} | Dallas Desi Scoop`;
  const metaDescription = `${name}${clinic ? ` (${clinic})` : ''} is a ${typeLabel.toLowerCase()} serving the South Asian community in ${cityLabel}. Find contact details, location, and more on Dallas Desi Scoop.`;

  const isMedical = types.some((t) => t === 'Doctor' || t === 'Dentist');
  const jsonLd = [
    {
      '@type': isMedical ? 'Physician' : 'LocalBusiness',
      '@id': `${BASE_URL}${canonicalPath}#business`,
      name,
      image,
      description: `${name}, ${typeLabel} serving the Dallas–Fort Worth South Asian community.`,
      telephone: phone || undefined,
      address: {
        '@type': 'PostalAddress',
        ...(address ? { streetAddress: address } : {}),
        addressLocality: location && location !== 'Other' ? location : 'Dallas',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      areaServed: 'Dallas–Fort Worth metroplex',
      url: contacts[0]?.url || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Health & Wellness', item: `${BASE_URL}/health-wellness` },
        { '@type': 'ListItem', position: 3, name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other health & wellness providers (deduped by slug).
  const seenSlugs = new Set<string>([slug as string]);
  const relatedProviders = mc.items
    .map((p: any) => ({ item: p, key: String(p._key || '') }))
    .filter((r: any) => {
      if (!r.key || seenSlugs.has(r.key)) return false;
      seenSlugs.add(r.key);
      return true;
    })
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${image}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/health-wellness"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Health &amp; Wellness
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <HeartPulse className="w-3.5 h-3.5 text-[#51ba4f]" />
              {typeLabel} · {cityLabel}
            </span>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {clinic ? `${name} practices at ${clinic}. ` : ''}
            {specialty ? `Specialty: ${specialty}. ` : ''}
            {name} is a {typeLabel.toLowerCase()} serving the South Asian community across the
            Dallas–Fort Worth metroplex{location && location !== 'Other' ? `, based in ${location}` : ''}.
            Contact {name} directly for appointments, consultations, and details on the full range
            of services offered.
          </p>

          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={28} />
            </div>
          )}
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {address && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{address}</span>
              </div>
            )}
            {phone && (
              <a
                href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{phone}</span>
              </a>
            )}
            {contacts.map((c) => (
              <a
                key={c.url}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <c.Icon className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{c.label}</span>
              </a>
            ))}
            {contacts.length === 0 && !phone && !address && (
              <p className="text-gray-400 text-[13px]">Contact information coming soon.</p>
            )}
          </div>
          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Get Directions
            </a>
          )}
        </div>
      </div>

      {/* Internal links: other providers */}
      {relatedProviders.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">
            More Health &amp; Wellness Providers in DFW
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {relatedProviders.map(({ item, key }: any) => (
              <Link
                key={key}
                to={`/health-wellness/${key}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <HeartPulse className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/health-wellness', label: 'All Health & Wellness in DFW' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/temples', label: 'Temples in DFW' },
            { to: '/salon-spa', label: 'Salons & Spas' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="bg-white border border-gray-100 rounded-full px-4 py-2 text-[12px] font-medium text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
